import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";

const tooltipStyles = {
  tooltip: {
    sx: {
      backgroundColor: "#121212",
      color: "#FFFFFF",
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 400,
      px: 1.5,
      py: 1,
      borderRadius: "8px",
      maxWidth: 320,
    },
  },
  arrow: { sx: { color: "#121212" } },
};

const CvSectionHeader = ({ title, subtitle, tooltipText, popupText }) => {
  const [open, setOpen] = useState(false);

  const { intro, sections, footer } = useMemo(() => {
    const paragraphs = popupText.split("\n\n").map((t) => t.trim()).filter(Boolean);
    if (paragraphs.length === 0) return { intro: "", sections: [], footer: "" };

    const intro = paragraphs[0];
    const footer = paragraphs.length > 1 ? paragraphs[paragraphs.length - 1] : "";
    const middle = paragraphs.length > 2 ? paragraphs.slice(1, -1) : [];

    const sections = [];
    let currentBullets = [];

    middle.forEach((p) => {
      if (p.endsWith(":")) {
        if (currentBullets.length) {
          sections.push({ type: "bullets", items: currentBullets });
          currentBullets = [];
        }
        sections.push({ type: "heading", text: p });
      } else {
        currentBullets.push(p);
      }
    });
    if (currentBullets.length) {
      sections.push({ type: "bullets", items: currentBullets });
    }

    return { intro, sections, footer };
  }, [popupText]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: 0.5 }}>
        <Tooltip title={tooltipText} placement="top" arrow slotProps={tooltipStyles}>
          <Typography
            variant="h6"
            sx={{ fontSize: "20px", lineHeight: "28px", cursor: "help", color: "text.primary" }}
          >
            {title}
          </Typography>
        </Tooltip>
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          aria-label={`Open ${title} guidance`}
          sx={{ p: 0.25 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.muted" }} />
        </IconButton>
      </Box>
      <Typography variant="sm" sx={{ fontWeight: 600, color: "text.muted" }}>
        {subtitle}
      </Typography>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "background.paper" } }}
      >
        <DialogTitle
          sx={{
            px: 4,
            pr: 6,
            pt: 3,
            pb: 1.5,
            typography: "dialogTitle",
          }}
        >
          {title}
          <IconButton
            aria-label="Close guidance modal"
            onClick={() => setOpen(false)}
            data-icon-style="close"
            sx={{ position: "absolute", right: 20, top: 20 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, pb: 3.5, pt: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {intro}
          </Typography>

          {sections.map((section, i) =>
            section.type === "heading" ? (
              <Typography
                key={i}
                variant="body2"
                sx={{ fontWeight: 600, mt: i > 0 ? 2 : 0 }}
              >
                {section.text}
              </Typography>
            ) : (
              <Box key={i} component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1 }}>
                {section.items.map((item, j) => (
                  <Box component="li" key={j} sx={{ mb: 0.5 }}>
                    <Typography variant="body2">{item}</Typography>
                  </Box>
                ))}
              </Box>
            )
          )}

          {footer && (
            <>
              <Divider sx={{ my: 2, borderColor: "grey.300" }} />
              <Typography variant="body2" sx={{ color: "text.muted" }}>
                {footer}
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

CvSectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  tooltipText: PropTypes.string.isRequired,
  popupText: PropTypes.string.isRequired,
};

export default CvSectionHeader;
